type AttemptStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value !== null && typeof value === 'object') {
    return `{${Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`)
      .join(',')}}`
  }
  return JSON.stringify(value) ?? 'null'
}

export function browserAttemptStorage(): AttemptStorage | undefined {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}

/** Persist only a UUID and payload digest, never applicant answers or email. */
export function createSubmissionAttemptStore(storage?: AttemptStorage) {
  const attempts = new Map<string, string>()
  async function keyFor(scope: string, payload: unknown): Promise<string> {
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(canonicalJson(payload))
    )
    const fingerprint = Array.from(new Uint8Array(digest), (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('')
    return `application-attempt:${scope}:${fingerprint}`
  }
  return {
    async get(scope: string, payload: unknown): Promise<string> {
      const key = await keyFor(scope, payload)
      const cached = attempts.get(key)
      if (cached) return cached
      let stored: string | null = null
      try {
        stored = storage?.getItem(key) ?? null
      } catch {
        /* Memory retries still work. */
      }
      const requestId =
        stored &&
        /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(stored)
          ? stored
          : crypto.randomUUID()
      attempts.set(key, requestId)
      try {
        storage?.setItem(key, requestId)
      } catch {
        /* Storage may be disabled. */
      }
      return requestId
    },
    async complete(scope: string, payload: unknown, requestId: string): Promise<void> {
      const key = await keyFor(scope, payload)
      if (attempts.get(key) !== requestId) return
      attempts.delete(key)
      try {
        if (storage?.getItem(key) === requestId) storage.removeItem(key)
      } catch {
        /* Optional storage. */
      }
    },
  }
}
